import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Book, Flash, Settings } from '@carbon/react/icons';
import { useAuthPermissions } from 'src/hooks/useAuthPermissions';
import { useBilling } from 'src/hooks/useBilling';

const DOCS_URL = import.meta.env.VITE_DOCS_URL;

/**
 * Docs, settings and the upgrade pill, sitting between the centred search and the
 * account divider. Kept as its own component (rather than inlined in MainHeader) so it
 * can be unit-tested without mounting the rest of the header shell.
 */
export const HeaderActions = React.memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canManageOrg } = useAuthPermissions();
  const { data: billing } = useBilling();

  return (
    <div className="shell-header-actions">
      {/* TODO: no docs site exists yet. Inert until VITE_DOCS_URL is set — the button
          renders (the reference design wants it visible) but does nothing on click. */}
      <button
        type="button"
        id="header-docs-button"
        className="shell-icon-button shell-header-actions__docs"
        aria-label={t('header.documentation')}
        title={t('header.documentation')}
        onClick={DOCS_URL ? () => window.open(DOCS_URL, '_blank', 'noopener,noreferrer') : undefined}
      >
        <Book size={20} />
      </button>

      {canManageOrg && (
        <button
          type="button"
          id="header-settings-button"
          className="shell-icon-button shell-header-actions__settings"
          aria-label={t('header.settings')}
          title={t('header.settings')}
          onClick={() => navigate('/settings/organization')}
        >
          <Settings size={20} />
        </button>
      )}

      {/* Same gate as the billing route itself, so a member is never offered a page that bounces them. */}
      {canManageOrg && billing?.enabled && billing.plan === 'free' && (
        <button
          type="button"
          id="header-upgrade-button"
          className="shell-upgrade-button"
          onClick={() => navigate('/settings/organization/billing')}
        >
          <Flash size={16} className="shell-upgrade-button__icon" />
          <span className="shell-upgrade-button__label">{t('header.upgrade')}</span>
        </button>
      )}
    </div>
  );
});
